import { tick } from 'svelte';

/**
 * Pages re-load their data whenever {@link sync.version} changes: after coming back online,
 * after the offline outbox is flushed, when the app regains focus, or on pull-to-refresh. It is the
 * lightweight stand-in for realtime updates until SignalR is added.
 */
class SyncState {
	version = $state(0);
	private loads = new Set<Promise<unknown>>();

	bump() {
		this.version++;
	}

	/** Registers a running data load, so {@link reload} can wait for it. */
	track<T>(load: Promise<T>): Promise<T> {
		this.loads.add(load);
		const forget = () => this.loads.delete(load);
		load.then(forget, forget);
		return load;
	}

	/** Bumps the version and resolves once the loads it set off are done, with whether they all succeeded. */
	async reload(): Promise<boolean> {
		this.bump();
		await tick();
		const results = await Promise.allSettled([...this.loads]);
		return results.every((result) => result.status === 'fulfilled');
	}
}

export const sync = new SyncState();

/**
 * A page's data load, tracked for pull-to-refresh. `loaded` turns true after the first load, which is when
 * the page drops its loading overlay.
 */
export class PageLoad {
	loaded = $state(false);

	constructor(private readonly load: () => Promise<unknown>) {}

	run(): Promise<unknown> {
		return sync.track(this.load()).finally(() => (this.loaded = true));
	}
}
